// Lib
import React, { useCallback } from 'react'
import { useFormikContext } from 'formik'
import { useSnackbar } from 'notistack'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'

// Include in project
import styles from './index.module.scss'
import { Modal, Table, Notification, Button } from 'component'
import { ETranformationDataIntoTable, transformationDataIntoTable } from 'utils/transformationDataIntoTable'
import { OrderInformationSection, EvidenceSection, CustomerInformationSection, SummayOrderSection } from 'container'
import { IOrderManagement } from 'pages/OrderManagement'
import { MutationUpdateOrderStatusArgs, MutationUpdateStripePaymentStatusArgs } from 'utils/generated'
import { ETransformationDataToServer, transformationDataToServer } from 'utils/transformationDataToServer'
import { EResCode } from 'utils/other'
import { convertCurrencyFormatDecimal } from 'utils/convertMoney'

interface Props {
  open: boolean
  handleClose: () => void
  updateOrderStatus: ({ variables }: { variables: MutationUpdateOrderStatusArgs }) => Promise<any>
  updateStripePaymentStatus: ({ variables }: { variables: MutationUpdateStripePaymentStatusArgs }) => Promise<any>
  refetch: () => void
}

const formatAddress = (addr: any) => {
  if (!addr) return '-'
  const parts = [addr.address, addr.subDistrict, addr.district, addr.province, addr.postCode].filter(Boolean)
  return parts.join(', ') || '-'
}

const ModalOrderInfo: React.FC<Props> = ({
  open,
  handleClose,
  updateOrderStatus,
  updateStripePaymentStatus,
  refetch,
}) => {
  const { values } = useFormikContext<IOrderManagement>()
  const { enqueueSnackbar } = useSnackbar()

  const dataTable = transformationDataIntoTable({
    type: ETranformationDataIntoTable.OrderProductList,
    dataList: values.formInput.orderItem,
  })

  const handleExportPDF = useCallback(async () => {
    const { formInput } = values
    const buyer = formInput.buyerInfo
    const shippingAddr = formInput.shippingAddress
    const billingAddr = formInput.billingAddress
    const items = formInput.orderItem || []

    // Build product rows
    const productRows = items
      .filter(Boolean)
      .map((item: any, idx: number) => {
        const itemPrice = item?.price || 0
        const itemQty = item?.qty || 0
        const itemTotal = itemPrice * itemQty
        return `
          <tr>
            <td style="text-align:center">${idx + 1}</td>
            <td>${item?.productName || '-'}</td>
            <td>${item?.productSKUName || '-'}</td>
            <td style="text-align:center">${itemQty}</td>
            <td style="text-align:right">${convertCurrencyFormatDecimal(itemPrice)}</td>
            <td style="text-align:right">${convertCurrencyFormatDecimal(itemTotal)}</td>
          </tr>`
      })
      .join('')

    const htmlContent = `
      <div id="pdf-export-content" style="
        width: 794px;
        padding: 40px;
        font-family: 'Helvetica Neue', Arial, sans-serif;
        font-size: 13px;
        color: #222;
        background: #fff;
        box-sizing: border-box;
      ">
        <!-- Title -->
        <div style="text-align:center; margin-bottom: 24px;">
          <h1 style="margin:0; font-size:22px; font-weight:700;">Order Details</h1>
          <p style="margin:4px 0 0; font-size:14px; color:#666;">Order No: ${formInput.orderNo || '-'}</p>
        </div>

        <!-- Order Info + Customer Info -->
        <div style="display:flex; gap:24px; margin-bottom:24px;">
          <!-- Customer Info -->
          <div style="flex:1; border:1px solid #ddd; border-radius:8px; padding:16px;">
            <h3 style="margin:0 0 12px; font-size:15px; font-weight:600; border-bottom:2px solid #333; padding-bottom:6px;">
              Customer Information
            </h3>
            <table style="width:100%; border-collapse:collapse; font-size:13px;">
              <tr><td style="padding:4px 0; color:#666; width:120px;">Name</td><td style="padding:4px 0; font-weight:500;">${
                buyer?.firstname || ''
              } ${buyer?.surname || ''}</td></tr>
              <tr><td style="padding:4px 0; color:#666;">Email</td><td style="padding:4px 0;">${
                buyer?.email || '-'
              }</td></tr>
              <tr><td style="padding:4px 0; color:#666;">Phone</td><td style="padding:4px 0;">${
                buyer?.phoneNumber || '-'
              }</td></tr>
              <tr><td style="padding:4px 0; color:#666; vertical-align:top;">Shipping Address</td><td style="padding:4px 0;">${formatAddress(
                shippingAddr,
              )}</td></tr>
              <tr><td style="padding:4px 0; color:#666; vertical-align:top;">Billing Address</td><td style="padding:4px 0;">${formatAddress(
                billingAddr,
              )}</td></tr>
              ${
                billingAddr?.taxID
                  ? `<tr><td style="padding:4px 0; color:#666;">Tax ID</td><td style="padding:4px 0;">${billingAddr.taxID}</td></tr>`
                  : ''
              }
            </table>
          </div>

          <!-- Order Info -->
          <div style="flex:0 0 220px; border:1px solid #ddd; border-radius:8px; padding:16px;">
            <h3 style="margin:0 0 12px; font-size:15px; font-weight:600; border-bottom:2px solid #333; padding-bottom:6px;">
              Order Info
            </h3>
            <table style="width:100%; border-collapse:collapse; font-size:13px;">
              <tr><td style="padding:4px 0; color:#666;">Order No</td><td style="padding:4px 0; font-weight:500;">${
                formInput.orderNo || '-'
              }</td></tr>
              <tr><td style="padding:4px 0; color:#666;">Shipping</td><td style="padding:4px 0;">${
                formInput.shippingType || '-'
              }</td></tr>
              <tr>
                <td style="padding:4px 0; color:#666;">Payment</td>
                <td style="padding:4px 0;">
                  <span style="
                    display:inline-block;
                    padding:2px 8px;
                    border-radius:4px;
                    font-size:11px;
                    font-weight:600;
                    background:${formInput.paymentStatus === 'PAID' ? '#e6f4ea' : '#fef7e0'};
                    color:${formInput.paymentStatus === 'PAID' ? '#1e7e34' : '#b8860b'};
                  ">${formInput.paymentStatus || '-'}</span>
                </td>
              </tr>
              <tr>
                <td style="padding:4px 0; color:#666;">Status</td>
                <td style="padding:4px 0;">
                  <span style="
                    display:inline-block;
                    padding:2px 8px;
                    border-radius:4px;
                    font-size:11px;
                    font-weight:600;
                    background:#e8f0fe;
                    color:#1a56db;
                  ">${formInput.orderStatus || '-'}</span>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <!-- Products Table -->
        <div style="margin-bottom:24px;">
          <h3 style="margin:0 0 12px; font-size:15px; font-weight:600; border-bottom:2px solid #333; padding-bottom:6px;">
            Products
          </h3>
          <table style="width:100%; border-collapse:collapse; font-size:13px;">
            <thead>
              <tr style="background:#f5f5f5;">
                <th style="border:1px solid #ddd; padding:8px; text-align:center; width:40px;">#</th>
                <th style="border:1px solid #ddd; padding:8px; text-align:left;">Product Name</th>
                <th style="border:1px solid #ddd; padding:8px; text-align:left;">SKU</th>
                <th style="border:1px solid #ddd; padding:8px; text-align:center; width:60px;">QTY</th>
                <th style="border:1px solid #ddd; padding:8px; text-align:right; width:100px;">Price (฿)</th>
                <th style="border:1px solid #ddd; padding:8px; text-align:right; width:100px;">Total (฿)</th>
              </tr>
            </thead>
            <tbody>
              ${
                productRows ||
                '<tr><td colspan="6" style="border:1px solid #ddd; padding:8px; text-align:center; color:#999;">No items</td></tr>'
              }
            </tbody>
          </table>
        </div>

        <!-- Summary -->
        <div style="display:flex; justify-content:flex-end;">
          <div style="width:300px; border:1px solid #ddd; border-radius:8px; padding:16px;">
            <h3 style="margin:0 0 12px; font-size:15px; font-weight:600; border-bottom:2px solid #333; padding-bottom:6px;">
              Summary
            </h3>
            <table style="width:100%; border-collapse:collapse; font-size:13px;">
              <tr>
                <td style="padding:6px 0; color:#666;">Product Price</td>
                <td style="padding:6px 0; text-align:right;">${convertCurrencyFormatDecimal(
                  formInput.price || 0,
                )} ฿</td>
              </tr>
              <tr>
                <td style="padding:6px 0; color:#666;">Discount</td>
                <td style="padding:6px 0; text-align:right; color:#e53935;">-${convertCurrencyFormatDecimal(
                  formInput.discount || 0,
                )} ฿</td>
              </tr>
              <tr>
                <td style="padding:6px 0; color:#666;">Shipping Fee</td>
                <td style="padding:6px 0; text-align:right;">${convertCurrencyFormatDecimal(
                  formInput.freight || 0,
                )} ฿</td>
              </tr>
              <tr>
                <td style="padding:6px 0; color:#666;">VAT 7%</td>
                <td style="padding:6px 0; text-align:right;">${convertCurrencyFormatDecimal(formInput.tax || 0)} ฿</td>
              </tr>
              <tr style="border-top:2px solid #333;">
                <td style="padding:10px 0 0; font-weight:700; font-size:15px;">Net Total</td>
                <td style="padding:10px 0 0; text-align:right; font-weight:700; font-size:15px;">${convertCurrencyFormatDecimal(
                  formInput.total || 0,
                )} ฿</td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    `

    // Create temporary element for rendering
    const tempContainer = document.createElement('div')
    tempContainer.style.position = 'fixed'
    tempContainer.style.left = '-9999px'
    tempContainer.style.top = '0'
    tempContainer.innerHTML = htmlContent
    document.body.appendChild(tempContainer)

    const exportEl = tempContainer.querySelector('#pdf-export-content') as HTMLElement

    try {
      const canvas = await html2canvas(exportEl, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      })

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('portrait', 'mm', 'a4')

      const pdfWidth = 210 // A4 portrait width
      const pdfHeight = 297 // A4 portrait height
      const margin = 10
      const contentWidth = pdfWidth - margin * 2
      const imgRatio = contentWidth / canvas.width
      const totalImgHeight = canvas.height * imgRatio
      const pageContentHeight = pdfHeight - margin * 2

      if (totalImgHeight <= pageContentHeight) {
        pdf.addImage(imgData, 'PNG', margin, margin, contentWidth, totalImgHeight)
      } else {
        // Multi-page
        const totalPages = Math.ceil(totalImgHeight / pageContentHeight)
        for (let page = 0; page < totalPages; page++) {
          if (page > 0) pdf.addPage()
          const srcY = (page * pageContentHeight) / imgRatio
          const srcH = Math.min(pageContentHeight / imgRatio, canvas.height - srcY)
          const pageCanvas = document.createElement('canvas')
          pageCanvas.width = canvas.width
          pageCanvas.height = srcH
          const ctx = pageCanvas.getContext('2d')
          if (ctx) {
            ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH)
            const pageImgData = pageCanvas.toDataURL('image/png')
            pdf.addImage(pageImgData, 'PNG', margin, margin, contentWidth, srcH * imgRatio)
          }
        }
      }

      const orderNo = formInput.orderNo || 'order'
      pdf.save(`${orderNo}.pdf`)

      enqueueSnackbar(<Notification title="Success" description="Export PDF successfully." />, {
        variant: 'success',
      })
    } catch (error) {
      enqueueSnackbar(<Notification title="Error" description="Failed to export PDF." />, {
        variant: 'error',
      })
    } finally {
      document.body.removeChild(tempContainer)
    }
  }, [values, enqueueSnackbar])

  const handleUpdateOrderStatus = async () => {
    const packData = await transformationDataToServer({
      values: values,
      type: ETransformationDataToServer.UpdateOrderStatus,
    })

    updateOrderStatus({ variables: packData as MutationUpdateOrderStatusArgs }).then((res) => {
      const resUpdate = res.data.updateOrderStatus
      if (resUpdate.res_code !== EResCode.Success) return

      refetch()
      handleClose()
      enqueueSnackbar(<Notification title="Success" description="Update Order Status." />, {
        variant: 'success',
      })
    })
  }

  const handleUpdateStripe = async () => {
    const packData = await transformationDataToServer({
      values: values,
      type: ETransformationDataToServer.UpdateStripePaymentStatus,
    })

    updateStripePaymentStatus({ variables: packData as MutationUpdateStripePaymentStatusArgs }).then((res) => {
      const resUpdate = res.data.updateStripePaymentStatus
      if (resUpdate.res_code !== EResCode.Success) return

      refetch()
      handleClose()
      enqueueSnackbar(<Notification title="Success" description="Update Order Status with stripe." />, {
        variant: 'success',
      })
    })
  }

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      width="90%"
      height="90%"
      title="Orders"
      buttonText="Save Order"
      handleSave={handleUpdateOrderStatus}
      extraHeaderButtons={
        <Button
          typeButton="button"
          type="primaryOutline"
          name="Export PDF"
          functionOnClick={handleExportPDF}
          startIcon={<PictureAsPdfIcon />}
        />
      }
    >
      <div className={styles.container}>
        <section className={styles.wrapperCard}>
          <h5>Orders</h5>
          <OrderInformationSection handleUpdateStripe={handleUpdateStripe} />
          <EvidenceSection />
          <CustomerInformationSection />
        </section>
        <section className={styles.wrapperCard}>
          <h5>Products</h5>
          <Table
            objectKey={{
              imgHTML: 'Image',
              productNameHTML: 'Name',
              qtyHTML: 'QTY.',
              priceHTML: 'Price (฿)',
              totalHTML: 'Total (฿)',
              note: 'Note',
            }}
            data={dataTable}
          />
          <SummayOrderSection />
        </section>
      </div>
    </Modal>
  )
}

export default ModalOrderInfo
